// Granola meeting picker. Lists the user's Granola meetings via MCP, lets
// them pick one + a call type, and runs the existing coaching analysis on
// the structured transcript. Result lands on /a/:id like a normal analysis.
//
// SPIKE: shape-defensive parsing. Granola's MCP can return list_meetings as
// either `structuredContent` or stringified JSON in `content[0].text`. If
// neither parses to a useful shape we fall back to a raw JSON dump so we can
// see what came back and adapt without redeploying.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { analyseGranolaMeeting, CALL_TYPE_LABELS, listGranolaMeetings, type CallType, type GranolaMeetingItem } from "../lib/api";
import { Crumbs } from "../components/Crumbs";
import { PageTitle } from "../components/PageTitle";

type ListState =
  | { kind: "loading" }
  | { kind: "empty" }
  | { kind: "ok"; meetings: GranolaMeetingItem[] }
  | { kind: "unparsed"; raw: unknown }
  | { kind: "error"; message: string };

const CALL_TYPES: ReadonlyArray<{ value: CallType; label: string }> = (Object.keys(CALL_TYPE_LABELS) as CallType[]).map(
  (value) => ({ value, label: CALL_TYPE_LABELS[value] }),
);

export function GranolaMeetings() {
  const navigate = useNavigate();
  const [state, setState] = useState<ListState>({ kind: "loading" });
  const [analysingId, setAnalysingId] = useState<string | null>(null);
  const [analyseError, setAnalyseError] = useState<string | null>(null);
  const [callTypeById, setCallTypeById] = useState<Record<string, CallType>>({});

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setState({ kind: "loading" });
    try {
      const res = await listGranolaMeetings();
      if (!res.ok) {
        setState({ kind: "error", message: res.error ?? "Could not list meetings." });
        return;
      }
      const items = res.items ?? [];
      if (items.length > 0) {
        setState({ kind: "ok", meetings: items });
      } else if (rawHasMeetings(res.raw)) {
        // Server got data back but couldn't parse any items -- surface raw for debugging.
        setState({ kind: "unparsed", raw: res.raw });
      } else {
        setState({ kind: "empty" });
      }
    } catch (err) {
      setState({ kind: "error", message: err instanceof Error ? err.message : String(err) });
    }
  }

  async function onAnalyse(meetingId: string) {
    setAnalyseError(null);
    setAnalysingId(meetingId);
    try {
      const callType = callTypeById[meetingId] ?? "one_on_one";
      const result = await analyseGranolaMeeting({ meetingId, callType });
      navigate(`/a/${result.id}`);
    } catch (err) {
      setAnalyseError(err instanceof Error ? err.message : String(err));
      setAnalysingId(null);
    }
  }

  return (
    <div style={{ maxWidth: 760 }}>
      <Crumbs trail={[{ label: "Settings", to: "/settings" }, { label: "Granola", to: "/connectors/granola" }, "Meetings"]} />
      <PageTitle lede="Pick a meeting to coach on. Klemento pulls the transcript on demand, labels speakers from Granola's Me/Them tags, and runs the same analysis as paste.">
        Your Granola meetings.
      </PageTitle>

      {analyseError ? (
        <div
          role="alert"
          style={{
            background: "rgb(var(--color-warning) / 0.08)",
            border: "1px solid rgb(var(--color-warning) / 0.3)",
            color: "rgb(var(--color-warning))",
            padding: "10px 14px",
            borderRadius: 10,
            marginBottom: 14,
            fontSize: 14,
          }}
        >
          {analyseError}
        </div>
      ) : null}

      {state.kind === "loading" ? <SkeletonRow count={3} /> : null}

      {state.kind === "empty" ? (
        <p style={{ color: "rgb(var(--color-ink-muted))" }}>
          No meetings found in your Granola account.
        </p>
      ) : null}

      {state.kind === "error" ? (
        <div>
          <p style={{ color: "rgb(var(--color-warning))" }}>{state.message}</p>
          <button type="button" className="btn btn-secondary" onClick={() => void load()} style={{ marginTop: 12 }}>
            Retry
          </button>
        </div>
      ) : null}

      {state.kind === "unparsed" ? (
        <div>
          <p style={{ color: "rgb(var(--color-ink-muted))" }}>
            Granola returned a shape we don't yet recognise. Raw response below so we can adapt the parser:
          </p>
          <pre
            style={{
              background: "rgb(var(--color-surface-strong))",
              padding: 16,
              borderRadius: 10,
              fontSize: 12,
              overflow: "auto",
              maxHeight: 480,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              marginTop: 12,
            }}
          >
            {JSON.stringify(state.raw, null, 2)}
          </pre>
        </div>
      ) : null}

      {state.kind === "ok" ? (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 12 }}>
          {state.meetings.map((m) => {
            const isAnalysing = analysingId === m.id;
            const ct = callTypeById[m.id] ?? "one_on_one";
            return (
              <li
                key={m.id}
                style={{
                  border: "1px solid rgb(var(--color-border-strong))",
                  borderRadius: 12,
                  padding: 16,
                  background: "rgb(var(--color-surface))",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start" }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4, overflowWrap: "anywhere" }}>
                      {m.title || "(untitled)"}
                    </div>
                    <div style={{ fontSize: 13, color: "rgb(var(--color-ink-muted))" }}>
                      {formatMeetingDate(m)}
                      {m.attendees?.length ? (formatMeetingDate(m) ? "  ·  " : "") + m.attendees.slice(0, 3).join(", ") : null}
                      {m.attendees && m.attendees.length > 3 ? ` +${m.attendees.length - 3}` : null}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                    <select
                      className="input"
                      value={ct}
                      onChange={(e) => setCallTypeById({ ...callTypeById, [m.id]: e.target.value as CallType })}
                      disabled={isAnalysing}
                      style={{ width: "auto", padding: "8px 10px", fontSize: 13 }}
                    >
                      {CALL_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => void onAnalyse(m.id)}
                      disabled={isAnalysing || analysingId !== null}
                      className="btn btn-primary"
                      style={{ fontSize: 13, padding: "8px 14px" }}
                    >
                      {isAnalysing ? "Analysing..." : "Analyse"}
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

function SkeletonRow({ count }: { count: number }) {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 12 }}>
      {Array.from({ length: count }).map((_, i) => (
        <li
          key={i}
          style={{
            border: "1px solid rgb(var(--color-border-strong))",
            borderRadius: 12,
            padding: 16,
            background: "rgb(var(--color-surface))",
            height: 72,
          }}
        >
          <div style={{ width: "60%", height: 14, background: "rgb(var(--color-surface-strong))", borderRadius: 4, marginBottom: 8 }} />
          <div style={{ width: "40%", height: 12, background: "rgb(var(--color-surface-strong))", borderRadius: 4 }} />
        </li>
      ))}
    </ul>
  );
}

/**
 * Server returned no items but we want to know whether that's "Granola has no
 * meetings" vs "server got data back but failed to parse any of it". Cheap
 * shape check on the raw response.
 */
function rawHasMeetings(raw: unknown): boolean {
  if (!raw || typeof raw !== "object") return false;
  const r = raw as Record<string, unknown>;
  if (Array.isArray(r.content)) {
    for (const item of r.content) {
      if (item && typeof item === "object") {
        const ti = item as { type?: string; text?: string };
        if (typeof ti.text === "string" && (ti.text.includes("<meeting ") || ti.text.includes('"meetings"'))) {
          return true;
        }
      }
    }
  }
  return false;
}

function formatMeetingDate(m: GranolaMeetingItem): string {
  if (m.date) {
    const d = new Date(m.date);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    }
  }
  if (m.rawDate) return m.rawDate;
  return "";
}
